import { ref, Ref, computed, onMounted, onBeforeUnmount, ComputedRef } from '@vue/composition-api';
import { useBroadcast, useBroadCastStream } from '@/common/utils';
import { debounce } from 'lodash';
import { useApolloModel } from 'vue-apollo-model';
import InteractiveModel from '@/components/video-interactive/model';
import { CurrentUserModel } from '@/models/current-user';
import { LoginSource } from '@/common/const';
import FollowModel from '@/components/user-card/model';
import fromEvent from 'xstream/extra/fromEvent';
import { Stream, Subscription } from 'xstream';
import VideoDetailModel from '@/models/video-detail';
import NewRecoFeedModel from '../model';
export function useKeyboard(callback: Function) {
    const playStreamSub = ref<Subscription>();
    let playFromKeydown: Stream<Event>;
    const broadcast = useBroadcast();
    onMounted(() => {
        playFromKeydown = fromEvent(window, 'keydown', false).filter(
            event =>
                (event as KeyboardEvent).keyCode === 38
                || (event as KeyboardEvent).keyCode === 40,
        );
        playStreamSub.value = playFromKeydown.subscribe({
            next: (e: Event) => {
                callback && callback(e);
                broadcast('key-down-trigger', {});
                e.preventDefault();
            },
        });
    });
    useBroadCastStream('input-focus', focus => {
        if (focus) {
            playStreamSub.value?.unsubscribe();
        } else {
            playStreamSub.value = playFromKeydown.subscribe({
                next: e => {
                    callback && callback(e);
                    e.preventDefault();
                },
            });
        }
    });
    onBeforeUnmount(() => {
        playStreamSub.value?.unsubscribe();
    });
}

export function useFeedInteractive(log: (logInfo: unknown) => void, {
    logType,
    currentPage,
    isFollowing,
}: {
    logType: string;
    currentPage: string;
    isFollowing: Ref<boolean>;
}) {
    const interactive = useApolloModel(InteractiveModel);
    const currentUserModel = useApolloModel(CurrentUserModel);
    const followModel = useApolloModel(FollowModel);
    const isLogin = computed(() => !!currentUserModel.userInfo?.id);
    const broadcast = useBroadcast();
    const waitingLike = ref(false);

    const handleVideoLike = async ({
        photoId,
        authorId,
        expTag,
        realLikeCount,
        liked,
        videoIndex,
        showLikeLottie,
    }: {
        photoId: string;
        authorId: string;
        expTag: string;
        realLikeCount: Ref<number>;
        liked: Ref<boolean>;
        videoIndex: number;
        showLikeLottie: (isLike: boolean) => void;
    }) => {
        log({
            type: 'CLICK',
            event_name: 'LIKE_PHOTO_BUTTON',
            event_value: {
                like_type: liked.value ? 'DISLIKE' : 'LIKE',
            },
        });
        if (!isLogin.value) {
            broadcast('login', {
                // 登录源，供日志上报使用
                source: LoginSource.LIKE,
                success: async () => {
                    handleVideoLike({ photoId, authorId, expTag, realLikeCount, liked, videoIndex, showLikeLottie });
                },
            });
            return;
        }
        const cancel = liked.value ? 1 : 0;
        if (waitingLike.value) {
            return;
        }
        waitingLike.value = true;
        await interactive.likeVideo({ photoId, photoAuthorId: authorId, cancel, expTag });
        const result = interactive.likeVideoResult;
        if (result === 1) {
            liked.value = !liked.value;
            if (realLikeCount.value < 10000) {
                if (cancel) {
                    realLikeCount.value--;
                } else {
                    realLikeCount.value++;
                }
            }
            showLikeLottie(liked.value);
            broadcast('reco-video-like', {
                liked: liked.value,
                count: realLikeCount.value,
                videoIndex,
            });
        }
        waitingLike.value = false;
    };

    const handleUninterested = async (photoId: string, videoType: number, expTag: string) => {
        log({
            type: 'CLICK',
            logType: logType,
            event_name: 'NO_INTEREST_BUTTON',
        });
        await interactive.feedbackVideo(photoId, videoType, expTag);
        return interactive.feedbackVideoResult;
    };

    const handleFollow = async ({ userId, expTag, videoIndex }: { userId: string; expTag: string; videoIndex: number; }) => {
        log({
            type: 'CLICK',
            event_name: 'USER_FOLLOW_BUTTON',
        });
        if (!isLogin.value) {
            broadcast('login', {
                // 登录源，供日志上报使用
                source: LoginSource.FOLLOW,
                success: async () => {
                    handleFollow({ userId, expTag, videoIndex });
                },
            });
        } else {
            await followModel.follow(userId, false, expTag);
            if (followModel.result === 1) {
                // 关注成功
                if (followModel.followStatus === 1) {
                    isFollowing.value = true;
                    broadcast('reco-follow', {
                        followed: isFollowing.value,
                        videoIndex,
                    });
                }
            }
        }
    };

    const handleReportPhoto = debounce(async (photoId: string, authorId: string) => {
        log({
            type: 'CLICK',
            event_name: 'REPORT_BUTTON',
            event_value: {
                btn_type: 'PHOTO',
            },
        });
        if (!isLogin.value) {
            broadcast('login', {});
            return;
        }
        broadcast('change-report-panel', {
            reportType: 1,
            page: currentPage,
            targetId: photoId,
            reportedUserId: authorId,
        });
    }, 300);

    return {
        handleFollow,
        handleVideoLike,
        handleUninterested,
        handleReportPhoto,
    };
}

export function useRefetchVideo(photoId: ComputedRef<string>) {
    const detailModel = useApolloModel(VideoDetailModel);
    const curPhoto = computed(() => detailModel.photo);
    useBroadCastStream('refetch-video', () => {
        detailModel.canRefetch = true;
        detailModel.fetchData(photoId.value);
    });
    return {
        curPhoto,
    };
}

export function useReco() {
    const feedModel = useApolloModel(NewRecoFeedModel);
    const feedList = computed(() => feedModel.feedVideoList);
    const llsid = computed(() => feedModel.llsid || '');
    function fetchMoreFeed() {
        feedModel.getMoreFeedList();
    }
    useBroadCastStream('reco-video-like', (data: { liked: boolean; count: number; videoIndex: number }) => {
        const { liked, count, videoIndex } = data;
        feedModel.updateVideoLike(videoIndex, liked, count);
    });
    useBroadCastStream('reco-follow', (data: { followed: boolean; videoIndex: number }) => {
        const { followed, videoIndex } = data;
        feedModel.updateAuthorFollow(videoIndex, followed);
    });
    return {
        feedList,
        llsid,
        fetchMoreFeed,
    };
}
